/* DS-260USA.com — landing-page translations
 * =============================================
 * Above-the-fold copy for each localized landing page. Rendered via
 * LandingPage's `t(key, locale)` helper. Adding a new locale = adding one
 * entry to LOCALES + one entry to STRINGS. That's it.
 *
 * Copy guidelines (consular-processing rewrite):
 *   • Product positioning: "Bring your family home — DS-260 done right"
 *   • Credibility hook: designed by a licensed U.S. immigration attorney
 *     with 35+ years of experience
 *   • Never use the words "AI paralegal", "LLM", "Claude", "Machine Learning"
 *   • Warm, respectful, second-person voice
 *   • Same testimonial across every locale (the attorney's own quote)
 */

export const LOCALES = [
  { code: "en", name: "English",       nativeName: "English",       flag: "🇺🇸", dir: "ltr" },
  { code: "es", name: "Spanish",       nativeName: "Español",       flag: "🇲🇽", dir: "ltr" },
  { code: "hi", name: "Hindi",         nativeName: "हिन्दी",         flag: "🇮🇳", dir: "ltr" },
  { code: "bn", name: "Bengali",       nativeName: "বাংলা",           flag: "🇧🇩", dir: "ltr" },
  { code: "pa", name: "Punjabi",       nativeName: "ਪੰਜਾਬੀ",         flag: "🇮🇳", dir: "ltr" },
  { code: "gu", name: "Gujarati",      nativeName: "ગુજરાતી",         flag: "🇮🇳", dir: "ltr" },
  { code: "ta", name: "Tamil",         nativeName: "தமிழ்",           flag: "🇮🇳", dir: "ltr" },
  { code: "te", name: "Telugu",        nativeName: "తెలుగు",          flag: "🇮🇳", dir: "ltr" },
  { code: "zh", name: "Mandarin",      nativeName: "中文",           flag: "🇨🇳", dir: "ltr" },
  { code: "tl", name: "Tagalog",       nativeName: "Tagalog",       flag: "🇵🇭", dir: "ltr" },
  { code: "vi", name: "Vietnamese",    nativeName: "Tiếng Việt",    flag: "🇻🇳", dir: "ltr" },
  { code: "ko", name: "Korean",        nativeName: "한국어",         flag: "🇰🇷", dir: "ltr" },
];

export const LOCALE_CODES = LOCALES.map((l) => l.code);

// The English trust-focused testimonial (attorney's own words). Rendered
// verbatim across every locale so the credibility signal never drifts.
const ATTORNEY_QUOTE = {
  en: "\"We built this platform to help families avoid the common paperwork mistakes we've seen in over three decades of family-based immigration practice — mistakes that can delay reunification by months or years.\"",
  es: "\"Construimos esta plataforma para ayudar a las familias a evitar los errores de papeleo que hemos visto en más de tres décadas de práctica en inmigración familiar — errores que pueden retrasar la reunificación por meses o años.\"",
  hi: "\"हमने यह मंच परिवारों को उन कागज़ी गलतियों से बचाने के लिए बनाया है, जिन्हें हम पारिवारिक इमिग्रेशन प्रैक्टिस के तीन दशकों से अधिक समय में देख रहे हैं — ऐसी गलतियाँ जो परिवार के मिलन में महीनों या वर्षों की देरी कर सकती हैं।\"",
  bn: "\"আমরা এই প্ল্যাটফর্মটি তৈরি করেছি পরিবারদের সেই সাধারণ কাগজপত্রের ভুল থেকে বাঁচাতে, যা তিন দশকেরও বেশি সময়ের পারিবারিক অভিবাসন অনুশীলনে আমরা দেখে এসেছি — যে ভুলগুলি পুনর্মিলনে মাস বা বছর দেরি করাতে পারে।\"",
  pa: "\"ਅਸੀਂ ਇਹ ਪਲੇਟਫਾਰਮ ਪਰਿਵਾਰਾਂ ਨੂੰ ਉਨ੍ਹਾਂ ਆਮ ਕਾਗਜ਼ੀ ਗਲਤੀਆਂ ਤੋਂ ਬਚਾਉਣ ਲਈ ਬਣਾਇਆ ਹੈ, ਜਿਹੜੀਆਂ ਅਸੀਂ ਤਿੰਨ ਦਹਾਕਿਆਂ ਤੋਂ ਵੱਧ ਦੇ ਪਰਿਵਾਰਕ ਇਮੀਗ੍ਰੇਸ਼ਨ ਅਭਿਆਸ ਵਿੱਚ ਦੇਖੀਆਂ ਹਨ — ਗਲਤੀਆਂ ਜੋ ਪੁਨਰ-ਮਿਲਨ ਨੂੰ ਮਹੀਨਿਆਂ ਜਾਂ ਸਾਲਾਂ ਲਈ ਦੇਰੀ ਕਰ ਸਕਦੀਆਂ ਹਨ।\"",
  gu: "\"અમે આ પ્લેટફોર્મ પરિવારોને એ સામાન્ય કાગળિયાની ભૂલોથી બચાવવા બનાવ્યું છે, જે અમે કૌટુંબિક ઇમિગ્રેશન પ્રેક્ટિસના ત્રણ દાયકાથી વધુ સમય માં જોઈ છે — ભૂલો જે પુનર્મિલનમાં મહિનાઓ કે વર્ષોનો વિલંબ કરી શકે છે.\"",
  ta: "\"குடும்ப அடிப்படையிலான குடியேற்ற பயிற்சியில் மூன்று தசாப்தங்களுக்கும் மேலாக நாங்கள் கண்ட பொதுவான ஆவணப் பிழைகளிலிருந்து குடும்பங்கள் தப்பிக்க உதவவே இந்த தளத்தை உருவாக்கினோம் — அந்தத் தவறுகள் மீண்டும் சேர்வதில் மாதங்கள் அல்லது ஆண்டுகள் தாமதத்தை ஏற்படுத்தக்கூடும்.\"",
  te: "\"కుటుంబ ఆధారిత ఇమిగ్రేషన్ ప్రాక్టీస్‌లో మేము మూడు దశాబ్దాలకు పైగా చూసిన సాధారణ పత్రాల తప్పుల నుండి కుటుంబాలను రక్షించడానికే మేము ఈ ప్లాట్‌ఫారమ్‌ను నిర్మించాము — ఆ తప్పులు పునఃసంఘటన లో నెలలు లేదా సంవత్సరాలు ఆలస్యం చేయగలవు.\"",
  zh: "\"我们打造这个平台,是为了帮助家庭避免我们在三十多年家庭移民实务中反复看到的那些常见文件错误——这些错误可能让家人团聚推迟数月甚至数年。\"",
  tl: "\"Ginawa namin ang platform na ito upang matulungan ang mga pamilya na maiwasan ang mga karaniwang pagkakamali sa mga papel na nakita namin sa mahigit tatlong dekada ng family-based immigration practice — mga pagkakamali na maaaring magpaantala sa muling pagsasama ng pamilya nang ilang buwan o taon.\"",
  vi: "\"Chúng tôi xây dựng nền tảng này để giúp các gia đình tránh những sai sót về giấy tờ phổ biến mà chúng tôi đã thấy trong hơn ba thập kỷ hành nghề di trú diện gia đình — những sai sót có thể khiến việc đoàn tụ bị trì hoãn hàng tháng hoặc hàng năm.\"",
  ko: "\"저희는 가족 기반 이민 실무를 30년 넘게 하면서 계속 보아온 흔한 서류 실수들로부터 가족들을 지키기 위해 이 플랫폼을 만들었습니다 — 그런 실수들은 가족 재결합을 몇 달, 심지어 몇 년까지 지연시킬 수 있습니다.\"",
};

const ATTORNEY_BYLINE = {
  en: "— Designed by a Licensed U.S. Immigration Attorney with 35+ years of experience",
  es: "— Diseñado por un abogado de inmigración licenciado en EE. UU. con más de 35 años de experiencia",
  hi: "— एक लाइसेंस प्राप्त अमेरिकी इमिग्रेशन अटॉर्नी द्वारा डिज़ाइन किया गया, 35+ वर्षों का अनुभव",
  bn: "— একজন লাইসেন্সপ্রাপ্ত মার্কিন অভিবাসন অ্যাটর্নি কর্তৃক ডিজাইনকৃত, ৩৫+ বছরের অভিজ্ঞতা",
  pa: "— ਲਾਇਸੰਸਸ਼ੁਦਾ ਯੂ.ਐਸ. ਇਮੀਗ੍ਰੇਸ਼ਨ ਅਟਾਰਨੀ ਵੱਲੋਂ ਡਿਜ਼ਾਈਨ ਕੀਤਾ, 35+ ਸਾਲਾਂ ਦਾ ਤਜਰਬਾ",
  gu: "— લાઇસન્સ ધરાવતા યુ.એસ. ઇમિગ્રેશન એટર્ની દ્વારા ડિઝાઇન કરાયેલ, 35+ વર્ષોનો અનુભવ",
  ta: "— உரிமம் பெற்ற அமெரிக்க குடிவரவு வழக்கறிஞரால் வடிவமைக்கப்பட்டது, 35+ ஆண்டுகள் அனுபவம்",
  te: "— లైసెన్స్ పొందిన యు.ఎస్. ఇమిగ్రేషన్ అటార్నీ చే రూపొందించబడింది, 35+ ఏళ్ల అనుభవం",
  zh: "— 由持牌美国移民律师设计,35 年以上执业经验",
  tl: "— Dinesenyo ng isang licensed U.S. immigration attorney na may mahigit 35 taong karanasan",
  vi: "— Được thiết kế bởi một luật sư di trú Hoa Kỳ có giấy phép hành nghề với hơn 35 năm kinh nghiệm",
  ko: "— 35년 이상의 경력을 지닌 미국 라이선스 이민 변호사가 설계",
};

// Default fallback = English. Any missing key falls back to en at runtime.
export const STRINGS = {
  en: {
    nav_signin: "Sign in",
    nav_get_started: "Get started",
    pill_new: "Consular Processing • DS-260 for the whole family",
    hero_title: "Turn Your Family's Documents Into Completed DS-260 Cases — Automatically",
    hero_subtitle:
      "Your I-130 is approved — now the consular stage begins. Upload or photograph passports, birth certificates, marriage certificates, and your NVC notice. DS-260USA.com reads documents in 15+ languages, extracts the information, creates a record for every family member, and prepares each person's DS-260 for ceac.state.gov — with little or no manual data entry. You review and approve everything before anything is submitted.",
    cta_primary: "Start free — Upload documents",
    cta_secondary: "See pricing",
    trust_bar: "Designed by a licensed U.S. immigration attorney with 35+ years of experience · You review before submitting · Not a law firm",
    f1_title: "Available in Multiple Languages",
    f1_body:
      "Consular processing is complicated enough. Language shouldn't be. Complete every family member's DS-260 in the language you're most comfortable using — and upload documents in their original language.",
    f2_title: "Upload From Your Phone",
    f2_body:
      "Snap a photo of a passport, birth certificate, or your NVC fee notice. Your information is automatically organized into each family member's DS-260 — you review every detail.",
    f3_title: "Interview-Ready Consular Packet",
    f3_body:
      "Every family member's DS-260 drafted, civil documents organized, and NVC fee steps tracked — from fee bill to consulate interview.",
    testimonial_title: "Why families choose DS-260USA.com",
    testimonial_body: ATTORNEY_QUOTE.en,
    testimonial_name: ATTORNEY_BYLINE.en,
    languages_pill: "12 languages",
    footer_switch: "Read this page in another language",
  },

  es: {
    nav_signin: "Iniciar sesión",
    nav_get_started: "Empezar",
    pill_new: "Proceso consular • DS-260 para toda la familia",
    hero_title: "Convierta los documentos de su familia en casos DS-260 completos — automáticamente",
    hero_subtitle:
      "Su I-130 fue aprobado — ahora comienza la etapa consular. Suba o fotografíe pasaportes, actas de nacimiento, actas de matrimonio y su aviso del NVC. DS-260USA.com lee documentos en más de 15 idiomas, extrae la información, crea un registro para cada familiar y prepara el DS-260 de cada persona para ceac.state.gov — con poca o ninguna captura manual. Usted revisa y aprueba todo antes de enviar.",
    cta_primary: "Empiece gratis — Suba sus documentos",
    cta_secondary: "Ver precios",
    trust_bar: "Diseñado por un abogado de inmigración licenciado con más de 35 años de experiencia · Usted revisa antes de enviar · No somos un bufete de abogados",
    f1_title: "Disponible en varios idiomas",
    f1_body:
      "El proceso consular ya es bastante complicado. El idioma no debería serlo. Complete el DS-260 de cada familiar en el idioma que más cómodo le resulte — y suba documentos en su idioma original.",
    f2_title: "Suba desde su teléfono",
    f2_body:
      "Tome una foto de un pasaporte, acta de nacimiento o su aviso de tarifas del NVC. Su información se organiza automáticamente en el DS-260 de cada familiar — usted revisa cada detalle.",
    f3_title: "Expediente consular listo para la entrevista",
    f3_body:
      "El DS-260 de cada familiar redactado, los documentos civiles organizados y los pagos del NVC bajo control — desde la factura hasta la entrevista consular.",
    testimonial_title: "Por qué las familias eligen DS-260USA.com",
    testimonial_body: ATTORNEY_QUOTE.es,
    testimonial_name: ATTORNEY_BYLINE.es,
    languages_pill: "12 idiomas",
    footer_switch: "Leer esta página en otro idioma",
  },

  hi: {
    nav_signin: "साइन इन",
    nav_get_started: "शुरू करें",
    pill_new: "कांसुलर प्रोसेसिंग • पूरे परिवार के लिए DS-260",
    hero_title: "अपने परिवार के दस्तावेज़ों को पूर्ण DS-260 केस में बदलें — स्वचालित रूप से",
    hero_subtitle:
      "आपका I-130 स्वीकृत हो गया — अब कांसुलर चरण शुरू होता है। पासपोर्ट, जन्म प्रमाणपत्र, विवाह प्रमाणपत्र और अपना NVC नोटिस अपलोड करें या फोटो खींचें। DS-260USA.com 15+ भाषाओं में दस्तावेज़ पढ़ता है, जानकारी निकालता है, हर परिवार सदस्य का रिकॉर्ड बनाता है, और ceac.state.gov के लिए हर व्यक्ति का DS-260 तैयार करता है — बहुत कम या बिना मैनुअल डेटा एंट्री के। जमा करने से पहले आप सब कुछ समीक्षा और अनुमोदन करते हैं।",
    cta_primary: "मुफ़्त शुरू करें — दस्तावेज़ अपलोड करें",
    cta_secondary: "मूल्य देखें",
    trust_bar: "35+ वर्षों के अनुभव वाले लाइसेंस प्राप्त अमेरिकी इमिग्रेशन अटॉर्नी द्वारा डिज़ाइन · जमा करने से पहले आप समीक्षा करते हैं · हम लॉ फ़र्म नहीं हैं",
    f1_title: "कई भाषाओं में उपलब्ध",
    f1_body:
      "कांसुलर प्रोसेसिंग पहले से ही जटिल है। भाषा नहीं होनी चाहिए। जिस भाषा में आप सहज हैं, उसी में हर परिवार सदस्य का DS-260 पूरा करें — और दस्तावेज़ उनकी मूल भाषा में अपलोड करें।",
    f2_title: "अपने फ़ोन से अपलोड करें",
    f2_body:
      "पासपोर्ट, जन्म प्रमाणपत्र या NVC फ़ीस नोटिस की एक तस्वीर लीजिए। आपकी जानकारी स्वचालित रूप से हर परिवार सदस्य के DS-260 में व्यवस्थित हो जाती है — आप हर विवरण की समीक्षा करते हैं।",
    f3_title: "इंटरव्यू के लिए तैयार कांसुलर पैकेट",
    f3_body:
      "हर परिवार सदस्य का DS-260 तैयार, नागरिक दस्तावेज़ व्यवस्थित, और NVC फ़ीस के चरण ट्रैक किए गए — फ़ीस बिल से लेकर कांसुलेट इंटरव्यू तक।",
    testimonial_title: "परिवार DS-260USA.com क्यों चुनते हैं",
    testimonial_body: ATTORNEY_QUOTE.hi,
    testimonial_name: ATTORNEY_BYLINE.hi,
    languages_pill: "12 भाषाएँ",
    footer_switch: "इस पृष्ठ को दूसरी भाषा में पढ़ें",
  },

  bn: {
    nav_signin: "সাইন ইন",
    nav_get_started: "শুরু করুন",
    pill_new: "কনস্যুলার প্রসেসিং • পুরো পরিবারের জন্য DS-260",
    hero_title: "আপনার পরিবারের নথিগুলিকে সম্পূর্ণ DS-260 কেসে পরিণত করুন — স্বয়ংক্রিয়ভাবে",
    hero_subtitle:
      "আপনার I-130 অনুমোদিত হয়েছে — এখন কনস্যুলার পর্যায় শুরু। পাসপোর্ট, জন্ম সনদ, বিবাহ সনদ এবং আপনার NVC নোটিশ আপলোড করুন বা ছবি তুলুন। DS-260USA.com ১৫+ ভাষায় নথি পড়ে, তথ্য বের করে, প্রতিটি পরিবার সদস্যের রেকর্ড তৈরি করে এবং ceac.state.gov-এর জন্য প্রত্যেকের DS-260 প্রস্তুত করে — খুব কম বা কোনো ম্যানুয়াল টাইপিং ছাড়াই। জমা দেওয়ার আগে আপনি সবকিছু পর্যালোচনা ও অনুমোদন করেন।",
    cta_primary: "বিনামূল্যে শুরু করুন — নথি আপলোড করুন",
    cta_secondary: "মূল্য দেখুন",
    trust_bar: "৩৫+ বছরের অভিজ্ঞতাসম্পন্ন লাইসেন্সপ্রাপ্ত মার্কিন অভিবাসন অ্যাটর্নি দ্বারা ডিজাইনকৃত · জমার আগে আপনি পর্যালোচনা করেন · আমরা ল ফার্ম নই",
    f1_title: "একাধিক ভাষায় উপলব্ধ",
    f1_body:
      "কনস্যুলার প্রসেসিং এমনিতেই যথেষ্ট জটিল। ভাষা হওয়া উচিত নয়। যে ভাষায় আপনি সবচেয়ে স্বাচ্ছন্দ্য বোধ করেন, সেই ভাষায় প্রতিটি পরিবার সদস্যের DS-260 সম্পূর্ণ করুন — এবং নথিগুলি তাদের মূল ভাষায় আপলোড করুন।",
    f2_title: "আপনার ফোন থেকে আপলোড করুন",
    f2_body:
      "পাসপোর্ট, জন্ম সনদ বা NVC ফি নোটিশের একটি ছবি তুলুন। আপনার তথ্য স্বয়ংক্রিয়ভাবে প্রতিটি পরিবার সদস্যের DS-260-এ সংগঠিত হয় — আপনি প্রতিটি বিবরণ পর্যালোচনা করেন।",
    f3_title: "ইন্টারভিউয়ের জন্য প্রস্তুত কনস্যুলার প্যাকেট",
    f3_body:
      "প্রতিটি পরিবার সদস্যের DS-260 খসড়া, নাগরিক নথি সংগঠিত, এবং NVC ফি-র ধাপগুলি ট্র্যাক করা — ফি বিল থেকে কনস্যুলেট ইন্টারভিউ পর্যন্ত।",
    testimonial_title: "পরিবারগুলি কেন DS-260USA.com বেছে নেয়",
    testimonial_body: ATTORNEY_QUOTE.bn,
    testimonial_name: ATTORNEY_BYLINE.bn,
    languages_pill: "১২টি ভাষা",
    footer_switch: "এই পৃষ্ঠাটি অন্য ভাষায় পড়ুন",
  },

  pa: {
    nav_signin: "ਸਾਈਨ ਇਨ",
    nav_get_started: "ਸ਼ੁਰੂ ਕਰੋ",
    pill_new: "ਕੌਂਸਲਰ ਪ੍ਰੋਸੈਸਿੰਗ • ਪੂਰੇ ਪਰਿਵਾਰ ਲਈ DS-260",
    hero_title: "ਆਪਣੇ ਪਰਿਵਾਰ ਦੇ ਦਸਤਾਵੇਜ਼ਾਂ ਨੂੰ ਪੂਰੇ DS-260 ਕੇਸਾਂ ਵਿੱਚ ਬਦਲੋ — ਆਪਣੇ ਆਪ",
    hero_subtitle:
      "ਤੁਹਾਡਾ I-130 ਮਨਜ਼ੂਰ ਹੋ ਗਿਆ — ਹੁਣ ਕੌਂਸਲਰ ਪੜਾਅ ਸ਼ੁਰੂ ਹੁੰਦਾ ਹੈ। ਪਾਸਪੋਰਟ, ਜਨਮ ਪ੍ਰਮਾਣ ਪੱਤਰ, ਵਿਆਹ ਪ੍ਰਮਾਣ ਪੱਤਰ ਅਤੇ ਆਪਣਾ NVC ਨੋਟਿਸ ਅੱਪਲੋਡ ਕਰੋ ਜਾਂ ਫ਼ੋਟੋ ਖਿੱਚੋ। DS-260USA.com 15+ ਭਾਸ਼ਾਵਾਂ ਵਿੱਚ ਦਸਤਾਵੇਜ਼ ਪੜ੍ਹਦਾ ਹੈ, ਜਾਣਕਾਰੀ ਕੱਢਦਾ ਹੈ, ਹਰ ਪਰਿਵਾਰਕ ਮੈਂਬਰ ਦਾ ਰਿਕਾਰਡ ਬਣਾਉਂਦਾ ਹੈ, ਅਤੇ ceac.state.gov ਲਈ ਹਰ ਵਿਅਕਤੀ ਦਾ DS-260 ਤਿਆਰ ਕਰਦਾ ਹੈ — ਬਹੁਤ ਘੱਟ ਜਾਂ ਬਿਨਾਂ ਹੱਥੀਂ ਡਾਟਾ ਐਂਟਰੀ ਦੇ। ਜਮ੍ਹਾਂ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਤੁਸੀਂ ਸਭ ਕੁਝ ਸਮੀਖਿਆ ਅਤੇ ਮਨਜ਼ੂਰ ਕਰਦੇ ਹੋ।",
    cta_primary: "ਮੁਫ਼ਤ ਸ਼ੁਰੂ ਕਰੋ — ਦਸਤਾਵੇਜ਼ ਅੱਪਲੋਡ ਕਰੋ",
    cta_secondary: "ਕੀਮਤਾਂ ਦੇਖੋ",
    trust_bar: "35+ ਸਾਲਾਂ ਦੇ ਤਜਰਬੇ ਵਾਲੇ ਲਾਇਸੰਸਸ਼ੁਦਾ ਯੂ.ਐਸ. ਇਮੀਗ੍ਰੇਸ਼ਨ ਅਟਾਰਨੀ ਵੱਲੋਂ ਡਿਜ਼ਾਈਨ ਕੀਤਾ · ਜਮ੍ਹਾਂ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਤੁਸੀਂ ਸਮੀਖਿਆ ਕਰਦੇ ਹੋ · ਅਸੀਂ ਲਾਅ ਫਰਮ ਨਹੀਂ ਹਾਂ",
    f1_title: "ਕਈ ਭਾਸ਼ਾਵਾਂ ਵਿੱਚ ਉਪਲਬਧ",
    f1_body:
      "ਕੌਂਸਲਰ ਪ੍ਰੋਸੈਸਿੰਗ ਪਹਿਲਾਂ ਹੀ ਕਾਫ਼ੀ ਗੁੰਝਲਦਾਰ ਹੈ। ਭਾਸ਼ਾ ਨਹੀਂ ਹੋਣੀ ਚਾਹੀਦੀ। ਜਿਸ ਭਾਸ਼ਾ ਵਿੱਚ ਤੁਸੀਂ ਸਭ ਤੋਂ ਵੱਧ ਆਰਾਮਦਾਇਕ ਹੋ ਉਸ ਵਿੱਚ ਹਰ ਪਰਿਵਾਰਕ ਮੈਂਬਰ ਦਾ DS-260 ਪੂਰਾ ਕਰੋ — ਅਤੇ ਦਸਤਾਵੇਜ਼ ਉਹਨਾਂ ਦੀ ਅਸਲੀ ਭਾਸ਼ਾ ਵਿੱਚ ਅੱਪਲੋਡ ਕਰੋ।",
    f2_title: "ਆਪਣੇ ਫ਼ੋਨ ਤੋਂ ਅੱਪਲੋਡ ਕਰੋ",
    f2_body:
      "ਪਾਸਪੋਰਟ, ਜਨਮ ਪ੍ਰਮਾਣ ਪੱਤਰ ਜਾਂ NVC ਫ਼ੀਸ ਨੋਟਿਸ ਦੀ ਇੱਕ ਫ਼ੋਟੋ ਲਓ। ਤੁਹਾਡੀ ਜਾਣਕਾਰੀ ਆਪਣੇ ਆਪ ਹਰ ਪਰਿਵਾਰਕ ਮੈਂਬਰ ਦੇ DS-260 ਵਿੱਚ ਵਿਵਸਥਿਤ ਹੋ ਜਾਂਦੀ ਹੈ — ਤੁਸੀਂ ਹਰ ਵੇਰਵੇ ਦੀ ਸਮੀਖਿਆ ਕਰਦੇ ਹੋ।",
    f3_title: "ਇੰਟਰਵਿਊ ਲਈ ਤਿਆਰ ਕੌਂਸਲਰ ਪੈਕੇਟ",
    f3_body:
      "ਹਰ ਪਰਿਵਾਰਕ ਮੈਂਬਰ ਦਾ DS-260 ਤਿਆਰ, ਸਿਵਲ ਦਸਤਾਵੇਜ਼ ਵਿਵਸਥਿਤ, ਅਤੇ NVC ਫ਼ੀਸ ਦੇ ਕਦਮ ਟ੍ਰੈਕ ਕੀਤੇ — ਫ਼ੀਸ ਬਿੱਲ ਤੋਂ ਕੌਂਸਲੇਟ ਇੰਟਰਵਿਊ ਤੱਕ।",
    testimonial_title: "ਪਰਿਵਾਰ DS-260USA.com ਕਿਉਂ ਚੁਣਦੇ ਹਨ",
    testimonial_body: ATTORNEY_QUOTE.pa,
    testimonial_name: ATTORNEY_BYLINE.pa,
    languages_pill: "12 ਭਾਸ਼ਾਵਾਂ",
    footer_switch: "ਇਸ ਪੰਨੇ ਨੂੰ ਹੋਰ ਭਾਸ਼ਾ ਵਿੱਚ ਪੜ੍ਹੋ",
  },

  gu: {
    nav_signin: "સાઇન ઇન",
    nav_get_started: "શરૂ કરો",
    pill_new: "કોન્સ્યુલર પ્રોસેસિંગ • આખા પરિવાર માટે DS-260",
    hero_title: "તમારા પરિવારના દસ્તાવેજોને સંપૂર્ણ DS-260 કેસમાં ફેરવો — આપોઆપ",
    hero_subtitle:
      "તમારું I-130 મંજૂર થયું — હવે કોન્સ્યુલર તબક્કો શરૂ થાય છે. પાસપોર્ટ, જન્મ પ્રમાણપત્ર, લગ્ન પ્રમાણપત્ર અને તમારો NVC નોટિસ અપલોડ કરો કે ફોટો લો. DS-260USA.com 15+ ભાષાઓમાં દસ્તાવેજો વાંચે છે, માહિતી કાઢે છે, દરેક પરિવાર સભ્યનો રેકોર્ડ બનાવે છે, અને ceac.state.gov માટે દરેક વ્યક્તિનું DS-260 તૈયાર કરે છે — બહુ ઓછી કે કોઈ મેન્યુઅલ ડેટા એન્ટ્રી વગર. સબમિટ કરતા પહેલા તમે બધું સમીક્ષા અને મંજૂર કરો છો.",
    cta_primary: "મફતમાં શરૂ કરો — દસ્તાવેજો અપલોડ કરો",
    cta_secondary: "કિંમતો જુઓ",
    trust_bar: "35+ વર્ષોના અનુભવ ધરાવતા લાઇસન્સ ધરાવતા યુ.એસ. ઇમિગ્રેશન એટર્ની દ્વારા ડિઝાઇન · સબમિટ કરતા પહેલા તમે સમીક્ષા કરો છો · અમે લૉ ફર્મ નથી",
    f1_title: "ઘણી ભાષાઓમાં ઉપલબ્ધ",
    f1_body:
      "કોન્સ્યુલર પ્રોસેસિંગ પહેલેથી જ પૂરતું જટિલ છે. ભાષા ન હોવી જોઈએ. જે ભાષામાં તમે સૌથી આરામદાયક હો તેમાં દરેક પરિવાર સભ્યનું DS-260 પૂરું કરો — અને દસ્તાવેજો તેમની મૂળ ભાષામાં અપલોડ કરો.",
    f2_title: "તમારા ફોનથી અપલોડ કરો",
    f2_body:
      "પાસપોર્ટ, જન્મ પ્રમાણપત્ર કે NVC ફી નોટિસનો ફોટો લો. તમારી માહિતી આપોઆપ દરેક પરિવાર સભ્યના DS-260માં ગોઠવાય છે — તમે દરેક વિગતની સમીક્ષા કરો છો.",
    f3_title: "ઇન્ટરવ્યૂ માટે તૈયાર કોન્સ્યુલર પેકેટ",
    f3_body:
      "દરેક પરિવાર સભ્યનું DS-260 તૈયાર, નાગરિક દસ્તાવેજો ગોઠવાયેલા, અને NVC ફીના પગલાં ટ્રૅક કરેલા — ફી બિલથી કોન્સ્યુલેટ ઇન્ટરવ્યૂ સુધી.",
    testimonial_title: "પરિવારો DS-260USA.com કેમ પસંદ કરે છે",
    testimonial_body: ATTORNEY_QUOTE.gu,
    testimonial_name: ATTORNEY_BYLINE.gu,
    languages_pill: "12 ભાષાઓ",
    footer_switch: "આ પૃષ્ઠને બીજી ભાષામાં વાંચો",
  },

  ta: {
    nav_signin: "உள்நுழை",
    nav_get_started: "தொடங்கு",
    pill_new: "தூதரக செயலாக்கம் • முழு குடும்பத்திற்கும் DS-260",
    hero_title: "உங்கள் குடும்பத்தின் ஆவணங்களை முழுமையான DS-260 வழக்குகளாக மாற்றுங்கள் — தானாகவே",
    hero_subtitle:
      "உங்கள் I-130 அங்கீகரிக்கப்பட்டது — இப்போது தூதரக கட்டம் தொடங்குகிறது. பாஸ்போர்ட், பிறப்புச் சான்றிதழ், திருமணச் சான்றிதழ் மற்றும் உங்கள் NVC அறிவிப்பை பதிவேற்றவும் அல்லது புகைப்படம் எடுக்கவும். DS-260USA.com 15+ மொழிகளில் ஆவணங்களை படித்து, தகவலை எடுத்து, ஒவ்வொரு குடும்ப உறுப்பினருக்கும் பதிவு உருவாக்கி, ceac.state.gov-க்கான ஒவ்வொருவரின் DS-260-ஐ தயார் செய்கிறது — கை முறையான உள்ளீடு இல்லாமல் அல்லது மிகக் குறைவாக. சமர்ப்பிக்கும் முன் அனைத்தையும் நீங்களே பரிசீலித்து ஒப்புதல் அளிக்கிறீர்கள்.",
    cta_primary: "இலவசமாக தொடங்குங்கள் — ஆவணங்களை பதிவேற்றுங்கள்",
    cta_secondary: "விலைகளை பாருங்கள்",
    trust_bar: "35+ ஆண்டுகள் அனுபவம் கொண்ட உரிமம் பெற்ற அமெரிக்க குடிவரவு வழக்கறிஞரால் வடிவமைக்கப்பட்டது · சமர்ப்பிக்கும் முன் நீங்களே பரிசீலிக்கிறீர்கள் · நாங்கள் ஒரு சட்ட நிறுவனம் அல்ல",
    f1_title: "பல மொழிகளில் கிடைக்கிறது",
    f1_body:
      "தூதரக செயலாக்கம் ஏற்கனவே போதுமான சிக்கலானது. மொழி அப்படி இருக்கக் கூடாது. உங்களுக்கு மிகவும் வசதியான மொழியில் ஒவ்வொரு குடும்ப உறுப்பினரின் DS-260-ஐயும் முடிக்கவும் — ஆவணங்களை அவற்றின் அசல் மொழியில் பதிவேற்றவும்.",
    f2_title: "உங்கள் ஃபோனிலிருந்து பதிவேற்றுங்கள்",
    f2_body:
      "பாஸ்போர்ட், பிறப்புச் சான்றிதழ் அல்லது NVC கட்டண அறிவிப்பின் புகைப்படம் எடுக்கவும். உங்கள் தகவல் தானாகவே ஒவ்வொரு குடும்ப உறுப்பினரின் DS-260-இல் ஒழுங்கமைக்கப்படுகிறது — ஒவ்வொரு விவரத்தையும் நீங்களே பரிசீலிக்கிறீர்கள்.",
    f3_title: "நேர்காணலுக்கு தயார் நிலை தூதரக தொகுப்பு",
    f3_body:
      "ஒவ்வொரு குடும்ப உறுப்பினரின் DS-260 வரைவு, சிவில் ஆவணங்கள் ஒழுங்கமைக்கப்பட்டு, NVC கட்டண படிகள் கண்காணிக்கப்படுகின்றன — கட்டண பில் முதல் தூதரக நேர்காணல் வரை.",
    testimonial_title: "குடும்பங்கள் DS-260USA.com ஐ ஏன் தேர்ந்தெடுக்கின்றன",
    testimonial_body: ATTORNEY_QUOTE.ta,
    testimonial_name: ATTORNEY_BYLINE.ta,
    languages_pill: "12 மொழிகள்",
    footer_switch: "இந்தப் பக்கத்தை மற்றொரு மொழியில் படிக்கவும்",
  },

  te: {
    nav_signin: "సైన్ ఇన్",
    nav_get_started: "ప్రారంభించండి",
    pill_new: "కాన్సులర్ ప్రాసెసింగ్ • మొత్తం కుటుంబానికి DS-260",
    hero_title: "మీ కుటుంబ పత్రాలను పూర్తి DS-260 కేసులుగా మార్చండి — స్వయంచాలకంగా",
    hero_subtitle:
      "మీ I-130 ఆమోదించబడింది — ఇప్పుడు కాన్సులర్ దశ ప్రారంభమవుతుంది. పాస్‌పోర్ట్‌లు, జనన ధృవీకరణ పత్రాలు, వివాహ ధృవీకరణ పత్రాలు మరియు మీ NVC నోటీసును అప్‌లోడ్ చేయండి లేదా ఫోటో తీయండి. DS-260USA.com 15+ భాషలలో పత్రాలను చదివి, సమాచారాన్ని సేకరించి, ప్రతి కుటుంబ సభ్యుని రికార్డు సృష్టించి, ceac.state.gov కోసం ప్రతి వ్యక్తి DS-260 ను సిద్ధం చేస్తుంది — చాలా తక్కువ లేదా మాన్యువల్ డేటా ఎంట్రీ లేకుండా. సమర్పించే ముందు మీరు అన్నింటినీ సమీక్షించి ఆమోదిస్తారు.",
    cta_primary: "ఉచితంగా ప్రారంభించండి — పత్రాలను అప్‌లోడ్ చేయండి",
    cta_secondary: "ధరలు చూడండి",
    trust_bar: "35+ ఏళ్ల అనుభవం ఉన్న లైసెన్స్‌డ్ యు.ఎస్. ఇమిగ్రేషన్ అటార్నీ చే రూపొందించబడింది · సమర్పించే ముందు మీరు సమీక్షిస్తారు · మేము లా ఫర్మ్ కాదు",
    f1_title: "బహుళ భాషలలో అందుబాటులో",
    f1_body:
      "కాన్సులర్ ప్రాసెసింగ్ ఇప్పటికే చాలా క్లిష్టంగా ఉంది. భాష ఉండకూడదు. మీరు అత్యంత సౌకర్యంగా ఉండే భాషలో ప్రతి కుటుంబ సభ్యుని DS-260 ను పూర్తి చేయండి — మరియు పత్రాలను వాటి అసలు భాషలో అప్‌లోడ్ చేయండి.",
    f2_title: "మీ ఫోన్ నుండి అప్‌లోడ్ చేయండి",
    f2_body:
      "పాస్‌పోర్ట్, జనన ధృవీకరణ పత్రం లేదా NVC ఫీజు నోటీసు ఫోటో తీయండి. మీ సమాచారం స్వయంచాలకంగా ప్రతి కుటుంబ సభ్యుని DS-260 లో నిర్వహించబడుతుంది — మీరు ప్రతి వివరాన్ని సమీక్షిస్తారు.",
    f3_title: "ఇంటర్వ్యూకు సిద్ధమైన కాన్సులర్ ప్యాకెట్",
    f3_body:
      "ప్రతి కుటుంబ సభ్యుని DS-260 ముసాయిదా, పౌర పత్రాలు నిర్వహించబడి, NVC ఫీజు దశలు ట్రాక్ చేయబడతాయి — ఫీజు బిల్లు నుండి కాన్సులేట్ ఇంటర్వ్యూ వరకు.",
    testimonial_title: "కుటుంబాలు DS-260USA.com ను ఎందుకు ఎంచుకుంటాయి",
    testimonial_body: ATTORNEY_QUOTE.te,
    testimonial_name: ATTORNEY_BYLINE.te,
    languages_pill: "12 భాషలు",
    footer_switch: "ఈ పేజీని మరో భాషలో చదవండి",
  },

  zh: {
    nav_signin: "登录",
    nav_get_started: "开始使用",
    pill_new: "领事程序 • 全家人的 DS-260",
    hero_title: "上传家庭文件,DS-260 案件自动完成",
    hero_subtitle:
      "您的 I-130 已获批 —— 现在进入领事程序阶段。只需上传或拍照您的护照、出生证、结婚证和 NVC 缴费通知。DS-260USA.com 能读取 15+ 种语言的文件,自动提取信息,为每位家庭成员建立档案,并为 ceac.state.gov 准备每个人的 DS-260 —— 几乎无需手动录入。您在提交前审阅并批准全部内容。",
    cta_primary: "免费开始 — 上传您的文件",
    cta_secondary: "查看价格",
    trust_bar: "由持牌美国移民律师设计,35 年以上执业经验 · 递交前由您亲自审阅 · 我们不是律师事务所",
    f1_title: "支持多种语言",
    f1_body:
      "领事程序本身已经够复杂了,语言不应该再成为障碍。用您最熟悉的语言完成每位家庭成员的 DS-260 —— 文件也可以用其原始语言上传。",
    f2_title: "用手机就能上传",
    f2_body:
      "给护照、出生证或 NVC 缴费通知拍一张照片。您的信息会自动整理进每位家庭成员的 DS-260 —— 每一个细节都由您亲自核对。",
    f3_title: "面签就绪的领事材料包",
    f3_body:
      "每位家庭成员的 DS-260 草稿、民事文件整理有序、NVC 缴费步骤全程跟踪 —— 从缴费单到领事馆面签。",
    testimonial_title: "为什么家庭选择 DS-260USA.com",
    testimonial_body: ATTORNEY_QUOTE.zh,
    testimonial_name: ATTORNEY_BYLINE.zh,
    languages_pill: "12 种语言",
    footer_switch: "以其他语言阅读本页",
  },

  tl: {
    nav_signin: "Mag-sign in",
    nav_get_started: "Magsimula",
    pill_new: "Consular Processing • DS-260 para sa buong pamilya",
    hero_title: "Gawin ang mga dokumento ng iyong pamilya na kumpletong DS-260 na kaso — awtomatiko",
    hero_subtitle:
      "Aprubado na ang inyong I-130 — nagsisimula na ang consular stage. I-upload o kunan lang ng litrato ang mga pasaporte, birth certificate, marriage certificate, at ang inyong NVC notice. Binabasa ng DS-260USA.com ang mga dokumento sa mahigit 15 wika, kinukuha ang impormasyon, gumagawa ng talaan para sa bawat miyembro ng pamilya, at inihahanda ang DS-260 ng bawat isa para sa ceac.state.gov — nang kaunting o walang manwal na pag-type. Kayo ang magsu-suri at mag-a-approve bago isumite.",
    cta_primary: "Magsimula nang libre — I-upload ang mga dokumento",
    cta_secondary: "Tingnan ang presyo",
    trust_bar: "Dinesenyo ng isang licensed U.S. immigration attorney na may mahigit 35 taong karanasan · Kayo ang nagsu-suri bago isumite · Hindi kami isang law firm",
    f1_title: "Magagamit sa maraming wika",
    f1_body:
      "Sapat nang kumplikado ang consular processing. Hindi na dapat maging problema ang wika. Kumpletuhin ang DS-260 ng bawat miyembro ng pamilya sa wikang pinaka-komportable kayo — at mag-upload ng mga dokumento sa kanilang orihinal na wika.",
    f2_title: "I-upload mula sa inyong telepono",
    f2_body:
      "Kumuha ng litrato ng pasaporte, birth certificate, o ng inyong NVC fee notice. Awtomatikong iaayos ang inyong impormasyon sa DS-260 ng bawat miyembro ng pamilya — kayo ang nagsu-suri sa bawat detalye.",
    f3_title: "Interview-ready na consular packet",
    f3_body:
      "Naka-draft ang DS-260 ng bawat miyembro ng pamilya, nakaayos ang mga civil document, at sinusubaybayan ang mga hakbang ng NVC fees — mula fee bill hanggang consulate interview.",
    testimonial_title: "Bakit pinipili ng mga pamilya ang DS-260USA.com",
    testimonial_body: ATTORNEY_QUOTE.tl,
    testimonial_name: ATTORNEY_BYLINE.tl,
    languages_pill: "12 wika",
    footer_switch: "Basahin ang pahinang ito sa ibang wika",
  },

  vi: {
    nav_signin: "Đăng nhập",
    nav_get_started: "Bắt đầu",
    pill_new: "Thủ tục lãnh sự • DS-260 cho cả gia đình",
    hero_title: "Biến giấy tờ gia đình bạn thành hồ sơ DS-260 hoàn chỉnh — tự động",
    hero_subtitle:
      "I-130 của bạn đã được chấp thuận — giờ là giai đoạn lãnh sự. Chỉ cần tải lên hoặc chụp ảnh hộ chiếu, giấy khai sinh, giấy đăng ký kết hôn và thông báo NVC của bạn. DS-260USA.com đọc giấy tờ bằng hơn 15 ngôn ngữ, trích xuất thông tin, tạo hồ sơ cho từng thành viên gia đình và chuẩn bị DS-260 của mỗi người cho ceac.state.gov — với rất ít hoặc không cần nhập liệu thủ công. Bạn xem lại và phê duyệt mọi thứ trước khi nộp.",
    cta_primary: "Bắt đầu miễn phí — Tải lên giấy tờ",
    cta_secondary: "Xem giá",
    trust_bar: "Được thiết kế bởi một luật sư di trú Hoa Kỳ có giấy phép hành nghề với hơn 35 năm kinh nghiệm · Bạn kiểm tra trước khi nộp · Chúng tôi không phải là công ty luật",
    f1_title: "Có sẵn nhiều ngôn ngữ",
    f1_body:
      "Thủ tục lãnh sự đã đủ phức tạp rồi. Ngôn ngữ thì không nên. Hoàn tất DS-260 của từng thành viên gia đình bằng ngôn ngữ bạn cảm thấy thoải mái nhất — và tải lên tài liệu bằng ngôn ngữ gốc của chúng.",
    f2_title: "Tải lên từ điện thoại",
    f2_body:
      "Chụp một tấm ảnh hộ chiếu, giấy khai sinh hay thông báo lệ phí NVC. Thông tin của bạn sẽ được tự động sắp xếp vào DS-260 của từng thành viên gia đình — bạn xem lại từng chi tiết.",
    f3_title: "Bộ hồ sơ lãnh sự sẵn sàng phỏng vấn",
    f3_body:
      "DS-260 của từng thành viên được soạn sẵn, giấy tờ hộ tịch được sắp xếp, các bước lệ phí NVC được theo dõi — từ hóa đơn lệ phí đến buổi phỏng vấn lãnh sự.",
    testimonial_title: "Vì sao các gia đình chọn DS-260USA.com",
    testimonial_body: ATTORNEY_QUOTE.vi,
    testimonial_name: ATTORNEY_BYLINE.vi,
    languages_pill: "12 ngôn ngữ",
    footer_switch: "Đọc trang này bằng ngôn ngữ khác",
  },

  ko: {
    nav_signin: "로그인",
    nav_get_started: "시작하기",
    pill_new: "영사 수속 • 온 가족의 DS-260",
    hero_title: "가족의 서류를 완성된 DS-260 케이스로 — 자동으로",
    hero_subtitle:
      "I-130이 승인되었습니다 — 이제 영사 수속 단계입니다. 여권, 출생증명서, 혼인증명서, NVC 통지서를 업로드하거나 사진만 찍으세요. DS-260USA.com은 15개 이상의 언어로 된 서류를 읽고, 정보를 추출해 가족 구성원별 기록을 만들며, ceac.state.gov 제출용 DS-260을 한 사람 한 사람 준비합니다 — 수동 입력은 거의 필요 없습니다. 제출 전 모든 내용을 직접 검토하고 승인하세요.",
    cta_primary: "무료로 시작 — 서류 업로드",
    cta_secondary: "가격 보기",
    trust_bar: "35년 이상의 경력을 지닌 미국 라이선스 이민 변호사가 설계 · 제출 전 여러분이 직접 검토 · 저희는 로펌이 아닙니다",
    f1_title: "다국어 지원",
    f1_body:
      "영사 수속만으로도 이미 충분히 복잡합니다. 언어는 그렇지 않아야 합니다. 가장 편한 언어로 가족 구성원 각각의 DS-260을 완성하세요 — 문서는 원본 언어로 업로드하셔도 됩니다.",
    f2_title: "휴대폰에서 바로 업로드",
    f2_body:
      "여권, 출생증명서, NVC 수수료 통지서를 사진 한 장으로 찍어 올리세요. 정보가 가족 구성원별 DS-260에 자동으로 정리됩니다 — 세부 사항은 여러분이 직접 검토하십니다.",
    f3_title: "인터뷰 준비 완료 영사 서류 패킷",
    f3_body:
      "가족 구성원별 DS-260 초안 작성, 민사 서류 정리, NVC 수수료 단계 추적 — 수수료 고지서부터 영사관 인터뷰까지.",
    testimonial_title: "가족들이 DS-260USA.com을 선택하는 이유",
    testimonial_body: ATTORNEY_QUOTE.ko,
    testimonial_name: ATTORNEY_BYLINE.ko,
    languages_pill: "12개 언어",
    footer_switch: "이 페이지를 다른 언어로 읽기",
  },
};

/** Return the translated string, falling back to English if missing. */
export function t(key, locale = "en") {
  const bundle = STRINGS[locale] || STRINGS.en;
  return bundle[key] ?? STRINGS.en[key] ?? key;
}

/** Return the locale metadata for a code, or English if unknown. */
export function localeMeta(code) {
  return LOCALES.find((l) => l.code === code) || LOCALES[0];
}
